import { styled } from '@mui/material';

import { Button } from '../../atoms/Button';
import { Tag } from '../../atoms/Tag';
import { type SuggestionItemProps, type SuggestionStatus, useSuggestionItem } from './useSuggestionItem';

type Props = SuggestionItemProps;

type OwnerState = {
  status: SuggestionStatus;
};

const Root = styled('article')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  ...(ownerState.status !== 'todo' && {
    backgroundColor: theme.palette.background.elevation1,
  }),
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

const Title = styled('h4')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.body1Emphasis,
  margin: 0,
  minWidth: 0,
  overflowWrap: 'anywhere',
  color: ownerState.status === 'ignored' ? theme.palette.text.secondary : theme.palette.text.primary,
}));

const Tags = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

const Meta = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  overflowWrap: 'anywhere',
  '& strong': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}));

const ToggleSlot = styled('div')({
  marginLeft: 'auto',
});

/**
 * One action detected automatically (by AI or rules) that the user can apply, ignore or reopen.
 * Follows the card action hierarchy: decisive action `contained`, helpers `text` default.
 */
export function SuggestionItem(props: Props) {
  const hook = useSuggestionItem(props);
  const ownerState = { status: props.status };

  return (
    <li>
      <Root ownerState={ownerState} data-testid={props['data-testid'] ?? 'suggestion-item'} data-status={props.status}>
        <Header>
          <Title ownerState={ownerState}>
            {props.title}
          </Title>
          {hook.stateTag ? <Tag color={hook.stateTag.color} variantIcon="state" label={hook.stateTag.label} /> : null}
        </Header>

        {props.tags ? <Tags>{props.tags}</Tags> : null}
        {props.meta ? <Meta>{props.meta}</Meta> : null}
        {hook.showDetails ? props.children : null}

        <Footer>
          {hook.isTodo ? (
            <Button
              variant="contained"
              size="small"
              startIcon={props.primaryAction.icon}
              loading={props.primaryAction.loading}
              disabled={props.primaryAction.disabled}
              onClick={props.primaryAction.onClick}
            >
              {props.primaryAction.label}
            </Button>
          ) : null}
          {props.secondaryAction ? (
            <Button
              variant="text"
              size="small"
              color="default"
              startIcon={props.secondaryAction.icon}
              loading={props.secondaryAction.loading}
              disabled={props.secondaryAction.disabled}
              onClick={props.secondaryAction.onClick}
            >
              {props.secondaryAction.label}
            </Button>
          ) : null}
          <ToggleSlot>
            <Button variant="text" size="small" color="default" onClick={props.onStatusToggle}>
              {hook.toggleLabel}
            </Button>
          </ToggleSlot>
        </Footer>
      </Root>
    </li>
  );
}

export type { Props as SuggestionItemProps };
